import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Helmet from 'react-helmet';
import {
  Box,
  Button,
  Container,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { fetchRewardSetBySlug } from '../utils/rewardsApi';
import RewardSetHero from '../components/CatalogoPremi/RewardSetHero';
import RewardCatalogCard from '../components/CatalogoPremi/RewardCatalogCard';
import RewardRedeemDrawer from '../components/CatalogoPremi/RewardRedeemDrawer';

const CatalogoPremiSet = () => {
  const { slug } = useParams();
  const { isAuthenticated, userData, requestUserData } = useAuth();
  const [rewardSet, setRewardSet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedReward, setSelectedReward] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const loadSet = async ({ silent = false } = {}) => {
    try {
      const response = await fetchRewardSetBySlug(slug);
      setRewardSet(response.data);
      setError('');
      return response.data;
    } catch (err) {
      if (!silent) throw err;
      return null;
    }
  };

  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        setLoading(true);
        await loadSet();
        if (!active) return;
        setError('');
      } catch (err) {
        if (!active) return;
        setRewardSet(null);
        setError(err.message === 'not-found' ? 'not-found' : 'generic');
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [slug]);

  const userPunti = Number(userData?.punti) || 0;

  const handleOpenRedeem = (reward) => {
    setSelectedReward(reward);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedReward(null);
  };

  const handleSuccess = async () => {
    await requestUserData({ withLoading: false });
    await loadSet({ silent: true });
  };

  if (loading) {
    return (
      <Box textAlign="center" py={20} bg="gray.50" minH="50vh">
        <Spinner size="lg" color="purple.500" />
      </Box>
    );
  }

  if (error === 'not-found') {
    return (
      <Container py={12}>
        <Heading size="md" mb={4}>
          Set premi non trovato
        </Heading>
        <Button as={Link} to="/catalogo-premi" colorScheme="purple">
          Torna al catalogo
        </Button>
      </Container>
    );
  }

  if (!rewardSet) {
    return (
      <Container py={12}>
        <Text>Errore nel caricamento.</Text>
      </Container>
    );
  }

  return (
    <Box bg="gray.50" minH="70vh" pb={10}>
      <Helmet>
        <title>{rewardSet.name} | Catalogo premi CIL</title>
      </Helmet>

      <RewardSetHero rewardSet={rewardSet} altFallback={rewardSet.name}>
        <VStack align="flex-start" spacing={3} color="white" maxW="3xl">
          <Heading
            as="h1"
            size={{ base: 'xl', md: '2xl' }}
            textShadow="0 2px 12px rgba(0,0,0,0.45)"
          >
            {rewardSet.name}
          </Heading>
          {rewardSet.description && (
            <Text
              fontSize={{ base: 'sm', md: 'md' }}
              opacity={0.95}
              textShadow="0 1px 6px rgba(0,0,0,0.4)"
              noOfLines={4}
            >
              {rewardSet.description}
            </Text>
          )}
          <Text fontSize="sm" opacity={0.85}>
            {rewardSet.rewards?.length || 0} premi disponibili
          </Text>
        </VStack>
      </RewardSetHero>

      <Container
        maxW="container.xl"
        py={{ base: 8, md: 10 }}
        px={{ base: 4, md: 6 }}
      >
        {rewardSet.rewards?.length > 0 ? (
          <SimpleGrid columns={{ base: 2, sm: 2, md: 3, lg: 4 }} spacing={5}>
            {rewardSet.rewards.map((reward) => (
              <RewardCatalogCard
                key={reward.documentId}
                reward={reward}
                onRedeem={handleOpenRedeem}
              />
            ))}
          </SimpleGrid>
        ) : (
          <Text color="gray.600">
            Nessun premio disponibile in questo set al momento.
          </Text>
        )}
      </Container>

      <RewardRedeemDrawer
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        reward={selectedReward}
        isAuthenticated={isAuthenticated}
        userPunti={userPunti}
        onSuccess={handleSuccess}
      />
    </Box>
  );
};

export default CatalogoPremiSet;
